import { supabase } from "@/lib/supabase";

export type ConciergeStatus = "draft" | "published" | "archived";

export type ConciergeSiteAdmin = {
  id: string;
  org_id: string;
  user_id: string;
  title: string | null;
  advisor_name: string | null;
  elevenlabs_agent_id: string | null;
  coach_user_id: string | null;
  status: ConciergeStatus;
  theme: any;
  created_at: string;
  updated_at: string;
};

export type ConciergeResourceAdmin = {
  id: string;
  site_id: string;
  type: string;
  title: string | null;
  description: string | null;
  url: string | null;
  storage_path: string | null;
  mime_type?: string | null;
  size_bytes?: number | null;
  duration_ms?: number | null;
  sort_order: number;
  created_at: string;
  updated_at: string;
};

export type ConciergeAgent = {
  id: string;
  org_id: string;
  label: string;
  elevenlabs_agent_id: string;
  active: boolean;
  created_at: string;
  updated_at: string;
};

export type OrgUserOption = {
  user_id: string;
  handle: string;
  has_site: boolean;
};

const sb = supabase as any;

// ---- sites -----------------------------------------------------------------

export async function listConciergeSites(orgId: string): Promise<ConciergeSiteAdmin[]> {
  const { data, error } = await sb
    .from("concierge_sites")
    .select("*")
    .eq("org_id", orgId)
    .order("updated_at", { ascending: false });
  if (error) throw error;
  return (data ?? []) as ConciergeSiteAdmin[];
}

export async function getConciergeSite(id: string): Promise<ConciergeSiteAdmin | null> {
  const { data, error } = await sb
    .from("concierge_sites")
    .select("*")
    .eq("id", id)
    .maybeSingle();
  if (error) throw error;
  return (data as ConciergeSiteAdmin) ?? null;
}

export async function createConciergeSite(input: {
  org_id: string;
  user_id: string;
  title: string;
  coach_user_id?: string | null;
}): Promise<ConciergeSiteAdmin> {
  const { data, error } = await sb
    .from("concierge_sites")
    .insert({
      org_id: input.org_id,
      user_id: input.user_id,
      title: input.title,
      status: "draft",
      theme: "dark",
      coach_user_id: input.coach_user_id ?? null,
    })
    .select("*")
    .single();
  if (error) throw error;
  return data as ConciergeSiteAdmin;
}

export async function updateConciergeSite(
  id: string,
  patch: Partial<
    Pick<ConciergeSiteAdmin, "title" | "advisor_name" | "elevenlabs_agent_id" | "coach_user_id">
  >,
): Promise<void> {
  const { error } = await sb.from("concierge_sites").update(patch).eq("id", id);
  if (error) throw error;
}

export async function setConciergeStatus(id: string, status: ConciergeStatus): Promise<void> {
  const { error } = await sb.from("concierge_sites").update({ status }).eq("id", id);
  if (error) throw error;
}

export async function deleteConciergeSite(id: string): Promise<void> {
  const { error } = await sb.from("concierge_sites").delete().eq("id", id);
  if (error) throw error;
}

// ---- resources -------------------------------------------------------------

export async function listResources(siteId: string): Promise<ConciergeResourceAdmin[]> {
  const { data, error } = await sb
    .from("concierge_resources")
    .select("*")
    .eq("site_id", siteId)
    .order("sort_order", { ascending: true });
  if (error) throw error;
  return (data ?? []) as ConciergeResourceAdmin[];
}

export async function createResource(input: {
  site_id: string;
  type: string;
  title?: string | null;
  description?: string | null;
  url?: string | null;
  sort_order: number;
}): Promise<ConciergeResourceAdmin> {
  const { data, error } = await sb
    .from("concierge_resources")
    .insert(input)
    .select("*")
    .single();
  if (error) throw error;
  return data as ConciergeResourceAdmin;
}

export async function updateResource(
  id: string,
  patch: Partial<Pick<ConciergeResourceAdmin, "type" | "title" | "description" | "url" | "sort_order" | "storage_path" | "mime_type" | "size_bytes" | "duration_ms">>,
): Promise<void> {
  const { error } = await sb.from("concierge_resources").update(patch).eq("id", id);
  if (error) throw error;
}

export async function deleteResource(id: string): Promise<void> {
  const { error } = await sb.from("concierge_resources").delete().eq("id", id);
  if (error) throw error;
}

export async function swapResourceOrder(
  a: { id: string; sort_order: number },
  b: { id: string; sort_order: number },
): Promise<void> {
  const e1 = await sb.from("concierge_resources").update({ sort_order: b.sort_order }).eq("id", a.id);
  if (e1.error) throw e1.error;
  const e2 = await sb.from("concierge_resources").update({ sort_order: a.sort_order }).eq("id", b.id);
  if (e2.error) throw e2.error;
}

// ---- users -----------------------------------------------------------------

export async function listOrgUsers(orgId: string): Promise<OrgUserOption[]> {
  const { data, error } = await sb.rpc("admin_list_concierge_org_users", { _org_id: orgId });
  if (error) throw error;
  return (data ?? []) as OrgUserOption[];
}

// ---- agents ----------------------------------------------------------------

export async function listConciergeAgents(orgId: string): Promise<ConciergeAgent[]> {
  const { data, error } = await sb
    .from("concierge_agents")
    .select("*")
    .eq("org_id", orgId)
    .order("active", { ascending: false })
    .order("label", { ascending: true });
  if (error) throw error;
  return (data ?? []) as ConciergeAgent[];
}

export async function createConciergeAgent(input: {
  org_id: string;
  label: string;
  elevenlabs_agent_id: string;
  active?: boolean;
}): Promise<ConciergeAgent> {
  const { data, error } = await sb
    .from("concierge_agents")
    .insert({ ...input, active: input.active ?? true })
    .select("*")
    .single();
  if (error) throw error;
  return data as ConciergeAgent;
}

export async function updateConciergeAgent(
  id: string,
  patch: Partial<Pick<ConciergeAgent, "label" | "elevenlabs_agent_id" | "active">>,
): Promise<void> {
  const { error } = await sb.from("concierge_agents").update(patch).eq("id", id);
  if (error) throw error;
}

export async function deleteConciergeAgent(id: string): Promise<void> {
  const { error } = await sb.from("concierge_agents").delete().eq("id", id);
  if (error) throw error;
}
